import { ParsedBackgroundCheck } from '@/backend/features/background-check/platform-functions/utils';
import { backgroundCheckStatusesSchema, backgroundCheckTypeSchema } from '@/backend/features/background-check/types';
import { EntityColumns, defineColumns } from '@juvare/platform-shared/features/storage';
import { z } from 'zod';

export const backgroundCheckHistoryEntityName = 'backgroundCheckHistory';
const backgroundCheckNoteMaxLength = 600;

const backgroundCheckDocumentSchema = z.object({
    entityKey: z.string(),
    name: z.string(),
    size: z.number(),
    url: z.string().trim().min(1),
});

export const backgroundCheckHistoryDataSchema = z.object({
    accountId: z.string().uuid(),
    type: backgroundCheckTypeSchema,
    prevStatus: backgroundCheckStatusesSchema.optional(),
    nextStatus: backgroundCheckStatusesSchema.optional(),
    requestId: z.string().uuid().optional(),
    requestedBy: z.string().optional(),
    checkResponseByRequestId: z.string().uuid().optional(),
    responseXml: z.string().optional(),
    request: z.any().optional(),
    message: z.string().optional(), // system generated
    note: z.string().max(backgroundCheckNoteMaxLength).optional(), // human entered
    document: backgroundCheckDocumentSchema.optional(),
    isUsingQuota: z.boolean().optional(),
    createdAt: z.number().optional(),
    isManualChange: z.boolean().optional(),
    parsedXml: z.custom<ParsedBackgroundCheck>().optional(), // JAS response - avoid validation
    internalStatusChange: z.boolean().optional(),
    isTestMode: z.boolean().optional(),
});

export type BackgroundCheckHistoryData = z.infer<typeof backgroundCheckHistoryDataSchema>;

export const backgroundCheckHistoryColumns = defineColumns({
    accountId: {
        type: 'id-required',
        index: true,
    },
    requestedBy: {
        type: 'string',
    },
    type: {
        type: 'string-required',
        index: true,
    },
    prevStatus: {
        type: 'string',
    },
    nextStatus: {
        type: 'string',
    },
    checkResponseByRequestId: {
        type: 'string',
        index: true,
    },
    message: {
        type: 'string',
    },
    note: {
        type: 'string',
    },
    createdAt: {
        type: 'number',
    },
    isUsingQuota: {
        type: 'boolean',
        index: true,
    },
    withDocument: {
        type: 'boolean-required',
    },
    isTestMode: {
        type: 'boolean',
    },
});

export type BackgroundCheckHistoryColumns = EntityColumns<typeof backgroundCheckHistoryColumns>;
